import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';

import ContractForm, { CONTRACT_FORM_MODAL_ID } from './ContractForm';
import TerminateContractModal, { TERMINATE_MODAL_ID } from './TerminateContractModal';
import ModalDocumentManager from './ModalDocumentManager';
import { ContractDA } from '../../../services/admin/contract.service';
import { Contract } from '../../../models/employee.model';
import { dateFormat } from '../../../scripts/date.time.format.script';
import AppPreloader from '../../../components/Preloader';

const CONTRACT_ROLES = ['employment_contract', 'amendment', 'other'] as const;

const DOC_MODAL_ID = 'modal-contract-documents';

interface ContractHistoryProps {
  companyId: string;
  employeeId: string;
  lang: any;
  onContractChange?: () => void;
}

const ContractHistory: React.FC<ContractHistoryProps> = ({ companyId, employeeId, lang, onContractChange }) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [terminateKey, setTerminateKey] = useState(0);
  const [showTerminate, setShowTerminate] = useState(false);
  const [docContractId, setDocContractId] = useState<string | null>(null);
  const [docModalKey, setDocModalKey] = useState(0);

  useEffect(() => {
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, employeeId]);

  const loadContracts = async () => {
    setLoading(true);
    const response = await ContractDA.getContracts(companyId, employeeId);
    if (response.status === 200) {
      const data = response.data;
      setContracts(Array.isArray(data) ? data : data?.items ?? []);
    } else {
      setContracts([]);
    }
    setLoading(false);
  };

  const handleSuccess = () => {
    setEditingContract(null);
    setShowTerminate(false);
    loadContracts();
    onContractChange?.();
  };

  const handleCancel = () => {
    setEditingContract(null);
    setShowTerminate(false);
  };

  const openCreateForm = () => {
    setEditingContract(null);
    setFormKey(k => k + 1);
  };

  const openEditForm = (contract: Contract) => {
    setEditingContract(contract);
    setFormKey(k => k + 1);
  };

  const openTerminateModal = () => {
    setShowTerminate(true);
    setTerminateKey(k => k + 1);
  };

  const handleCancelTermination = async (contract: Contract) => {
    const confirmed = await Swal.fire({
      title: lang.labels.cancelTermination || 'Cancel termination',
      text: lang.labels.cancelTerminationConfirm || 'Are you sure you want to cancel this termination? The employee will be set back to active status.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.confirm || 'Confirm',
      cancelButtonText: lang.labels.cancel || 'Cancel',
    });

    if (!confirmed.isConfirmed) return;

    const response = await ContractDA.cancelTermination(companyId, employeeId, contract.id);

    if (response.status === 200) {
      Swal.fire({
        title: lang.labels.terminationCancelled || 'Termination cancelled',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
      loadContracts();
      onContractChange?.();
    } else {
      Swal.fire({
        title: lang.labels.error || 'Error',
        text: response.data?.message || response.message || 'An error occurred',
        icon: 'error',
      });
    }
  };

  const openDocModal = (contractId: string) => {
    setDocContractId(contractId);
    setDocModalKey(k => k + 1);
  };

  const handleDocChanged = () => {
    loadContracts();
  };

  const currentContract = contracts.find(c => !c.valid_to);
  const nextVersion = contracts.length > 0
    ? Math.max(...contracts.map(c => c.version || 0)) + 1
    : 1;

  const isTerminated = currentContract?.end_reason != null;
  const reasonLabel = (reason: string) => lang.labels[`endReason_${reason}`] || reason.replace(/_/g, ' ');

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h4 className="fw-bolder mb-0">{lang.labels.contractHistory || 'Contract history'}</h4>
        <div className="d-flex gap-2">
          {currentContract && !isTerminated && (
            <button className="btn btn-danger btn-sm" onClick={openTerminateModal}>
              <i className="bi bi-x-circle me-1"></i>
              {lang.labels.terminateContract || 'Terminate contract'}
            </button>
          )}
          <button className="btn btn-primary" onClick={openCreateForm}>
            <i className="bi bi-plus-lg me-1"></i>
            {lang.labels.addContractVersion || 'Add contract version'}
          </button>
        </div>
      </div>

      {/* Termination status banner */}
      {currentContract && isTerminated && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between mb-5">
          <div>
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>{lang.labels.contractTerminated || 'Contract terminated'}</strong>
            {' — '}
            {currentContract.end_date ? dateFormat(lang.code, currentContract.end_date) : ''}
            {currentContract.end_reason && (
              <span className="ms-2 badge badge-light-dark">{reasonLabel(currentContract.end_reason)}</span>
            )}
          </div>
          <button
            className="btn btn-sm btn-light-warning"
            onClick={() => handleCancelTermination(currentContract)}
          >
            {lang.labels.cancelTermination || 'Cancel termination'}
          </button>
        </div>
      )}

      {loading ? (
        <AppPreloader />
      ) : contracts.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted fw-bold">{lang.labels.noContracts || 'No contracts yet'}</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table align-middle table-row-dashed mb-2">
              <thead className="border-bottom">
                <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                  <th>{lang.labels.version || 'Version'}</th>
                  <th>{lang.labels.contractType || 'Type'}</th>
                  <th>{lang.labels.validFrom || 'From'}</th>
                  <th>{lang.labels.endDate || 'End date'}</th>
                  <th>{lang.labels.activityRate}</th>
                  <th>{lang.labels.salary || 'Salary'}</th>
                  <th>{lang.labels.status || 'Status'}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="text-secondary fw-bold">
                {contracts.map(contract => {
                  const isCurrent = !contract.valid_to;
                  const docCount = (contract.vouchers ?? []).length;

                  return (
                    <tr key={contract.id}>
                      <td className="fw-bold">v{contract.version}</td>
                      <td>{lang.labels[`contractType_${contract.contract_type}`] || contract.contract_type}</td>
                      <td>{contract.valid_from ? dateFormat(lang.code, contract.valid_from) : '-'}</td>
                      <td>{contract.end_date ? dateFormat(lang.code, contract.end_date) : '-'}</td>
                      <td>{contract.activity_rate != null ? `${contract.activity_rate}%` : '-'}</td>
                      <td>
                        {contract.base_salary?.amount != null
                          ? `${contract.base_salary.amount} ${contract.base_salary.currency || 'CHF'}`
                          : '-'}
                      </td>
                      <td>
                        {isCurrent && isTerminated ? (
                          <span className="badge badge-light-danger">
                            {lang.labels.terminated || 'Terminated'}
                          </span>
                        ) : (
                          <span className={`badge ${isCurrent ? 'badge-light-success' : 'badge-light-secondary'}`}>
                            {isCurrent ? (lang.labels.current || 'Current') : (lang.labels.closed || 'Closed')}
                          </span>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-1">
                          <button
                            className={`btn btn-sm btn-icon ${docCount > 0 ? 'btn-light-primary' : 'btn-light'}`}
                            title={lang.labels.documents || 'Documents'}
                            onClick={() => openDocModal(contract.id)}
                          >
                            <i className="bi bi-paperclip"></i>
                            {docCount > 0 && <span className="ms-1 fs-8">{docCount}</span>}
                          </button>
                          <button
                            className="btn btn-sm btn-icon btn-light-primary"
                            title={lang.labels.edit}
                            onClick={() => openEditForm(contract)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ContractForm
        key={formKey}
        companyId={companyId}
        employeeId={employeeId}
        currentContract={currentContract}
        contractToEdit={editingContract}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        lang={lang}
        nextVersion={nextVersion}
        autoOpen={formKey > 0}
      />

      {showTerminate && currentContract && (
        <TerminateContractModal
          key={terminateKey}
          companyId={companyId}
          employeeId={employeeId}
          contract={currentContract}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          lang={lang}
          autoOpen
        />
      )}

      {docContractId && (() => {
        const contract = contracts.find(c => c.id === docContractId);
        return (
          <ModalDocumentManager
            key={docModalKey}
            modalId={DOC_MODAL_ID}
            title={`${lang.labels.contractDocuments || 'Contract documents'} — v${contract?.version ?? '?'}`}
            companyId={companyId}
            entityType="contract"
            entityId={docContractId}
            employeeId={employeeId}
            vouchers={contract?.vouchers ?? []}
            roles={[...CONTRACT_ROLES]}
            lang={lang}
            onChanged={handleDocChanged}
            autoOpen
          />
        );
      })()}
    </div>
  );
};

export default ContractHistory;
